import React, { useState, useEffect, useMemo } from 'react';
import {
  Check, X, RefreshCw, Pause, ChevronDown, ChevronRight, Download, RotateCcw,
} from 'lucide-react';
import { useFlowExecution } from '../../hooks/useFlowExecution';
import { getFlowStatConfig } from '../../config/flowStatisticsConfig';
import type { MetricCardConfig } from '../../config/flowStatisticsConfig';
import { StatusBadge, formatDate, formatDuration } from './FlowHelpers';
import { api } from '../../services/api';

// =====================
// Types
// =====================

interface FlowResultPanelProps {
  flowId: string;
  executionId: string | null;
  onNewRun?: () => void;
}

interface StepState {
  stepId: string;
  name: string;
  status: string;
  order: number;
  startedAt?: string;
  completedAt?: string;
  durationMs?: number;
  rowCountIn?: number;
  rowCountOut?: number;
  error?: string;
  retryCount?: number;
  progressMessage?: string;
  progress?: number;
}

// =====================
// Step Status Icon (kompakt)
// =====================

const StepIcon: React.FC<{ status: string }> = ({ status }) => {
  const base = 'w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0';
  switch (status) {
    case 'completed':
      return <div className={`${base} bg-green-100`}><Check size={12} className="text-green-800" /></div>;
    case 'failed':
      return <div className={`${base} bg-red-100`}><X size={12} className="text-red-800" /></div>;
    case 'running':
      return <div className={`${base} bg-yellow-100`}><RefreshCw size={12} className="text-yellow-800 animate-spin" /></div>;
    case 'paused':
      return <div className={`${base} bg-blue-100`}><Pause size={12} className="text-blue-800" /></div>;
    default:
      return <div className={`${base} bg-gray-100 border-2 border-gray-300`}><div className="w-2 h-2 rounded-full bg-gray-400" /></div>;
  }
};

// =====================
// Resolve nested value from resultSummary
// =====================

function resolveValue(obj: Record<string, any>, card: MetricCardConfig): any {
  const base = obj[card.key];
  if (card.nestedKey && base && typeof base === 'object') {
    return base[card.nestedKey] ?? 0;
  }
  return base ?? 0;
}

// =====================
// FlowResultPanel
// =====================

const FlowResultPanel: React.FC<FlowResultPanelProps> = ({ flowId, executionId, onNewRun }) => {
  const { execution, loading, error } = useFlowExecution(executionId ?? undefined);
  const [detailRows, setDetailRows] = useState<Record<string, any>[]>([]);
  const [detailLoading, setDetailLoading] = useState(false);
  const [stepsExpanded, setStepsExpanded] = useState(false);

  const isTerminal = execution?.status === 'completed' || execution?.status === 'failed' || execution?.status === 'cancelled';
  const isRunning = execution?.status === 'running' || execution?.status === 'paused';

  const config = useMemo(() => getFlowStatConfig(flowId, execution?.resultSummary), [flowId, execution?.resultSummary]);

  const steps: StepState[] = execution?.stepStates || [];

  // Lade Detail-Rows wenn Execution abgeschlossen
  useEffect(() => {
    if (!execution || !isTerminal || !executionId) return;

    const resultSummary = execution.resultSummary;

    // Option 1: rows direkt in resultSummary.results
    if (resultSummary?.results && Array.isArray(resultSummary.results)) {
      setDetailRows(resultSummary.results);
      return;
    }

    // Option 2: statisticsRows aus Step-Output des letzten Steps
    if (steps.length === 0 || config.detailColumns.length === 0) return;

    // Finde den letzten completed Step (der die Ergebnisdaten hat)
    const completedSteps = steps.filter(s => s.status === 'completed');
    if (completedSteps.length === 0) return;
    const lastStep = completedSteps[completedSteps.length - 1];

    setDetailLoading(true);
    api.flows.getStepData(executionId, lastStep.stepId)
      .then(res => {
        if (res.success && res.data?.outputData?.statisticsRows) {
          setDetailRows(res.data.outputData.statisticsRows);
        }
      })
      .catch(() => {})
      .finally(() => setDetailLoading(false));
  }, [execution?.status, executionId]);

  // Nichts rendern wenn keine executionId
  if (!executionId) return null;

  // Loading
  if (loading && !execution) {
    return (
      <div className="p-6 text-center">
        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-[#1A1A1A] mx-auto" />
        <span className="text-sm text-gray-500 mt-2 block">Lade Ausführung...</span>
      </div>
    );
  }

  // Error
  if (error || !execution) {
    return (
      <div className="text-center p-6">
        <X size={32} className="text-red-500 mx-auto" />
        <p className="mt-3 text-sm text-gray-500">{error || 'Ausführung nicht gefunden'}</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* ── Header: Status + Meta ─────────────────────────────── */}
      <div className="bg-white border border-[#DAD9D9] rounded-lg p-4">
        <div className="flex items-center gap-4 flex-wrap">
          <StatusBadge status={execution.status} large />
          <div className="text-sm text-gray-500">
            {formatDate(execution.startedAt)}
          </div>
          {execution.durationMs != null && (
            <div className="text-sm text-gray-500">
              Dauer: <span className="font-medium text-[#1A1A1A]">{formatDuration(execution.durationMs)}</span>
            </div>
          )}
          {execution.startedByUser?.username && (
            <div className="text-sm text-gray-500">
              von <span className="font-medium text-[#1A1A1A]">{execution.startedByUser.username}</span>
            </div>
          )}

          {/* Actions */}
          <div className="ml-auto flex gap-2">
            {isTerminal && onNewRun && (
              <button
                onClick={onNewRun}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-[#E9FF57] text-[#1A1A1A] rounded text-sm font-medium hover:bg-[#d4ea4d]"
              >
                <RotateCcw size={14} />
                Nochmal starten
              </button>
            )}
          </div>
        </div>
      </div>

      {/* ── Error Message ─────────────────────────────────────── */}
      {execution.errorMessage && (
        <div className="bg-red-50 border border-red-200 text-red-800 rounded-lg p-4 text-sm font-mono">
          {execution.errorMessage}
        </div>
      )}

      {/* ── Steps (während laufend: offen, nach Erfolg: eingeklappt) ── */}
      {steps.length > 0 && (
        <div className="bg-white border border-[#DAD9D9] rounded-lg">
          <button
            onClick={() => setStepsExpanded(e => !e)}
            className="w-full flex items-center gap-2 px-4 py-3 text-sm font-medium text-[#1A1A1A] hover:bg-gray-50 rounded-lg"
          >
            {stepsExpanded || isRunning ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
            Steps ({steps.length})
            {isRunning && (
              <span className="text-xs text-gray-400 ml-1">
                — {steps.filter(s => s.status === 'completed').length}/{steps.length} abgeschlossen
              </span>
            )}
          </button>

          {(stepsExpanded || isRunning) && (
            <div className="px-4 pb-4 space-y-1">
              {steps.map(step => (
                <div key={step.stepId} className="flex items-center gap-3 py-1.5">
                  <StepIcon status={step.status} />
                  <span className="text-sm text-[#1A1A1A] font-medium flex-1">{step.name}</span>
                  <StatusBadge status={step.status} />
                  {step.durationMs != null && (
                    <span className="text-xs text-gray-400">{formatDuration(step.durationMs)}</span>
                  )}
                  {step.status === 'running' && step.progressMessage && (
                    <span className="text-xs text-gray-500 italic">
                      {step.progressMessage}
                      {step.progress != null && <span className="ml-1 text-gray-400">({step.progress}%)</span>}
                    </span>
                  )}
                  {step.error && (
                    <span className="text-xs text-red-600 max-w-xs truncate" title={step.error}>
                      {step.error}
                    </span>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* ── Statistik-Cards (nur nach Abschluss) ─────────────── */}
      {isTerminal && execution.resultSummary && config.cards.length > 0 && (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
          {config.cards.map((card, i) => (
            <div key={`${card.key}-${i}`} className="bg-white border border-[#DAD9D9] rounded-lg p-3">
              <p className="text-xs text-gray-500">{card.label}</p>
              <p className="text-2xl font-bold" style={{ color: card.color || '#1A1A1A' }}>
                {resolveValue(execution.resultSummary, card)}
              </p>
            </div>
          ))}
        </div>
      )}

      {/* ── Excel-Download ────────────────────────────────────── */}
      {isTerminal && execution.resultSummary?.excelBase64 && (
        <button
          onClick={() => {
            const link = document.createElement('a');
            link.href = `data:application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;base64,${execution.resultSummary.excelBase64}`;
            link.download = `${flowId}-ergebnis.xlsx`;
            link.click();
          }}
          className="inline-flex items-center gap-2 px-4 py-2 bg-white border border-[#DAD9D9] rounded-lg hover:bg-gray-50 text-sm"
        >
          <Download className="w-4 h-4" />
          Excel herunterladen
        </button>
      )}

      {/* ── Detail-Tabelle (nur nach Abschluss + wenn Spalten definiert) ── */}
      {isTerminal && config.detailColumns.length > 0 && (
        <div className="bg-white border border-[#DAD9D9] rounded-lg overflow-hidden">
          {detailLoading ? (
            <div className="p-6 text-center">
              <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-[#1A1A1A] mx-auto" />
              <span className="text-xs text-gray-400 mt-2 block">Lade Details...</span>
            </div>
          ) : detailRows.length > 0 ? (
            <div className="overflow-x-auto max-h-[600px] overflow-y-auto">
              <table className="w-full text-sm">
                <thead className="sticky top-0 z-10 bg-white">
                  <tr className="border-b border-[#DAD9D9]">
                    {config.detailColumns.map(col => (
                      <th
                        key={col.key}
                        className="text-left py-2 px-3 text-[11px] text-gray-500 font-semibold uppercase tracking-wide"
                        style={{ width: col.width }}
                      >
                        {col.label}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {detailRows.map((row, i) => (
                    <tr key={i} className="border-b border-gray-100 hover:bg-gray-50">
                      {config.detailColumns.map(col => {
                        const val = row[col.key];
                        let display: React.ReactNode = val ?? '-';
                        let cellClass = 'py-2 px-3 text-sm';

                        // Status-Spalte: Farbige Icons
                        if (col.key === 'status' || col.label.toLowerCase() === 'status') {
                          if (val === '✓' || val === true) {
                            display = <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-green-100"><Check size={14} className="text-green-600" /></span>;
                          } else if (val === '✗' || val === false) {
                            display = <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-red-100"><X size={14} className="text-red-600" /></span>;
                          } else if (val === '!' ) {
                            display = <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-yellow-100 text-yellow-700 text-xs font-bold">!</span>;
                          } else if (val === '?') {
                            display = <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-gray-100 text-gray-500 text-xs font-bold">?</span>;
                          }
                        }
                        // Euro-Formatierung (Spalten mit € im Label oder Diff)
                        else if (val !== null && val !== undefined && val !== '-') {
                          const label = col.label.toLowerCase();
                          if (label.includes('(€)') || label.includes('diff')) {
                            const num = typeof val === 'number' ? val : parseFloat(String(val));
                            if (!isNaN(num)) {
                              display = num.toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' €';
                              if (label.includes('diff')) {
                                cellClass += num === 0 ? ' text-green-600' : ' text-red-600';
                              }
                            }
                          }
                          // Prozent-Formatierung (1:1 aus altem Tool: >30% = gruen/normal, <=30% = rot/verdaechtig)
                          else if (label.includes('%')) {
                            const num = typeof val === 'number' ? val : parseFloat(String(val));
                            if (!isNaN(num)) {
                              display = num.toFixed(1) + '%';
                              cellClass += Math.abs(num) > 30 ? ' text-green-600 font-semibold' : ' text-red-600 font-semibold';
                            }
                          }
                        }

                        return (
                          <td key={col.key} className={cellClass}>
                            {display}
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="p-4 text-center text-sm text-gray-400">Keine Detail-Daten vorhanden</div>
          )}
        </div>
      )}
    </div>
  );
};

export default FlowResultPanel;
